import { createSlice } from '@reduxjs/toolkit'

// Список поддерживаемых магазинов
export const SHOP_TYPES = ['eshop', 'steam', 'psn'];

// Начальное состояние одного магазина
const shopInitialState = {
   entities: {}, // Объект с данными заказа
   isLoading: false, // Флаг загрузки
   regionList: [], // Список доступных регионов
   regionListLoaded: false, // Флаг загрузки списка регионов
   currentRegion: null, // Выбранный регион
   error: null, // Текущая ошибка
   lastError: null, // Последняя ошибка
   orderStatus: null, // Статус заказа
   payUrl: null, // URL для оплаты
   billCreated: false, // Флаг создания счёта
};

// Начальное состояние слайса: нормализованный стейт по магазинам
const initialState = {
   activeShop: null, // Идентификатор активного магазина
   checkoutVisible: false, // Глобальный флаг видимости формы оформления заказа (применяется ко всем магазинам одновременно)
   shops: {
      eshop: { ...shopInitialState },
      steam: { ...shopInitialState },
      psn:   { ...shopInitialState },
   },
};

/**
 * Возвращает срез состояния для указанного магазина.
 * Если магазин не найден — возвращает shopInitialState (защита от ошибок).
 */
const getShopState = (state, shop) => state.shops[shop] ?? shopInitialState;

export const orderSlice = createSlice({
   name: 'order',
   initialState,
   reducers: {
      /**
       * Устанавливает активный магазин.
       * @param {Object} action - { payload: { shop: string } }
       */
      activeShopChanged: (state, action) => {
         state.activeShop = action.payload.shop;
      },

      /**
       * Устанавливает новый статус заказа для указанного магазина.
       * @param {Object} action - { payload: { shop: string, data: any } }
       */
      orderStatusChanged: (state, action) => {
         const { shop, data } = action.payload;
         getShopState(state, shop).orderStatus = data;
      },

      /**
       * Обрабатывает ошибку при запросе для указанного магазина.
       * @param {Object} action - { payload: { shop: string, message: string, status: number|null } }
       */
      errorAdded: (state, action) => {
         const { shop, message, status } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.error = { message, status };
         shopState.lastError = shopState.error;
         shopState.isLoading = false;
         shopState.entities = {};
         shopState.payUrl = null;
         shopState.billCreated = false;
         shopState.orderStatus = null;
      },

      /**
       * Обрабатывает не критичную ошибку при запросе для указанного магазина.
       * @param {Object} action - { payload: { shop: string, message: string, status: number|null } }
       */
      warningAdded: (state, action) => {
         const { shop, message, status, criticalError } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.error = { message, status };
         shopState.lastError = null;
         shopState.isLoading = false;
      },

      /**
       * Очищает ошибку для указанного магазина.
       * @param {Object} action - { payload: { shop: string } }
       */
      errorCleared: (state, action) => {
         const { shop } = action.payload;
         getShopState(state, shop).error = null;
      },

      /**
       * Изменяет выбранный регион для указанного магазина.
       * @param {Object} action - { payload: { shop: string, region: string|null } }
       */
      regionChanged: (state, action) => {
         const { shop, region } = action.payload;
         getShopState(state, shop).currentRegion = region;
      },

      /**
       * Сбрасывает флаг загрузки списка регионов перед запросом.
       * @param {Object} action - { payload: { shop: string } }
       */
      regionListRequested: (state, action) => {
         const { shop } = action.payload;
         getShopState(state, shop).regionListLoaded = false;
      },

      /**
       * Сохраняет полученный список регионов для указанного магазина.
       * @param {Object} action - { payload: { shop: string, regionList: Array } }
       */
      regionListReceived: (state, action) => {
         const { shop, regionList } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.regionList = regionList;
         shopState.regionListLoaded = true;
      },

      /**
       * Начинает процесс загрузки заказа для указанного магазина.
       * @param {Object} action - { payload: { shop: string } }
       */
      orderRequested: (state, action) => {
         const { shop } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.isLoading = true;
         shopState.error = null;
      },

      /**
       * Сохраняет полученный заказ с сервера для указанного магазина.
       * @param {Object} action - { payload: { shop: string, data: Object } }
       */
      orderReceived: (state, action) => {
         const { shop, data } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.entities = data;
         shopState.isLoading = false;
         shopState.error = null;
         shopState.payUrl = data.payUrl || null;
         shopState.billCreated = data.billCreated || false;
         shopState.orderStatus = null;
      },

      /**
       * Обновляет данные заказа для указанного магазина.
       * @param {Object} action - { payload: { shop: string, data: Object } }
       */
      orderChanged: (state, action) => {
         const { shop, data } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.entities = data;
         shopState.isLoading = false;
         shopState.error = null;
      },

      /**
       * Обрабатывает генерацию платёжного счёта для указанного магазина.
       * @param {Object} action - { payload: { shop: string, payUrl: string } }
       */
      billGenerated: (state, action) => {
         const { shop, payUrl } = action.payload;
         const shopState = getShopState(state, shop);
         shopState.isLoading = false;
         shopState.payUrl = payUrl;
         shopState.billCreated = true;
      },

      /**
       * Управляет глобальной видимостью формы оформления заказа (применяется ко всем магазинам одновременно).
       * @param {Object} action - { payload: { value: boolean } }
       */
      checkoutVisibleChanged: (state, action) => {
         const { value } = action.payload;
         state.checkoutVisible = value;
      },
   },
})

export const {
   activeShopChanged,
   orderReceived,
   orderChanged,
   regionListRequested,
   regionListReceived,
   regionChanged,
   errorAdded,
   warningAdded,
   orderStatusChanged,
   errorCleared,
   orderRequested,
   billGenerated,
   checkoutVisibleChanged,
} = orderSlice.actions

export default orderSlice.reducer
